import { HistoryEntry } from "../types/calculator";
import { CurrentPlan, SuggestedDosage } from "../types/data";

export function generatePlanDescription(plan: CurrentPlan | SuggestedDosage): string {
  const {
    dosage,
    dosage_units,
    timing,
    doses_per_interval,
    frequency_interval,
    days_on,
    days_off,
    duration_period,
    duration_repeat,
  } = plan;

  const dosagePart = dosage && dosage_units ? `${dosage} ${dosage_units}` : "a specified amount";
  const timingDescriptions = {
    AM: "in the morning",
    PM: "in the evening",
    "AM before exercise": "in the morning before exercise",
    midday: "at midday",
    "AM/PM": "in the morning or evening",
    "before bed": "before bed",
    anytime: "at any time",
  };
  
  const timingPart = timing !== "N/A"
    ? `to be taken ${timingDescriptions[timing] || timing.toLowerCase()}`
    : "at any time";

  // Calculate and describe dosing frequency based on doses_per_interval and frequency_interval
  const frequencyPart = (() => {
    const normalizedInterval = (frequency_interval ?? '').trim().toLowerCase();
    const doses = doses_per_interval || 1; // Default to 1 dose per interval if not specified

    if (normalizedInterval === "1 day" || normalizedInterval === "1 days") {
      return doses === 1 ? "daily" : `${doses} times daily`;
    }
    if (normalizedInterval === "1 week" || normalizedInterval === "1 weeks") {
      return doses === 1 ? "weekly" : `${doses} times weekly`;
    }
    if (normalizedInterval === "2 day" || normalizedInterval === "2 days") {
      return doses === 1 ? "every other day" : `${doses} doses every other day`;
    }

    return doses === 1
      ? `every ${frequency_interval}`
      : `${doses} doses every ${frequency_interval}`;
  })();

  const cyclePart =
    duration_period && parseDurationYears(duration_period) >= 1
      ? "continuously without breaks"
      : days_on
      ? `for ${days_on} days on`
      : "continuously";

  const offDaysPart = days_off ? `followed by ${days_off} days off` : "";
  const durationPart = duration_period ? `over ${duration_period}` : "";

  const zeroPattern = /^0\s*(min|minute|minutes|hour|hours|day|days|week|weeks|month|months|year|years)$/;
  const repeatPart =
    duration_repeat && !zeroPattern.test(duration_repeat)
      ? `and repeated every ${duration_repeat}`
      : "";

  return [
    "This regimen involves",
    dosagePart,
    timingPart,
    frequencyPart,
    cyclePart,
    offDaysPart,
    durationPart,
    repeatPart,
  ]
    .filter(Boolean)
    .join(" ");
}

export function generatePlanSummary(plan: CurrentPlan): string {
  switch (plan.type) {
    case "peptide":
      return `Inject ${plan.name}`;
    case "supplement":
      return `Take ${plan.name}`;
    case "food":
      return `Eat ${plan.name}`;
    case "drink":
      return `Drink ${plan.name}`;
    case "medication":
      return `Take medication: ${plan.name}`;
    case "herbal":
      return `Take herbal remedy: ${plan.name}`;
    case "activity":
      return `Perform activity: ${plan.name}`;
    default:
      return `Use ${plan.name}`;
  }
}

export function parseDurationYears(duration: string): number {
  const match = duration.match(/(\d+)\s*(week|month|year|day)s?/i);
  if (!match) return 0;

  const value = parseInt(match[1], 10);
  const unit = match[2].toLowerCase();

  switch (unit) {
    case "week":
      return value / 52;
    case "month":
      return value / 12;
    case "year":
      return value;
    case "day":
      return value / 365;
    default:
      return 0;
  }
}

interface EnhancedPlanDescription {
  description: string;
  injectionDetails?: string;
}

export function generateEnhancedPlanDescription(
  plan: CurrentPlan,
  history: HistoryEntry[]
): EnhancedPlanDescription {
  const baseDescription = generatePlanDescription(plan);

  const relevantCalculation = history
    .filter(entry => entry.inputs.peptideName === plan.name)
    .sort((a, b) => b.timestamp - a.timestamp)[0];

  if (!relevantCalculation) {
    return { description: baseDescription };
  }

  const { metrics } = relevantCalculation;
  const { inputs } = relevantCalculation;

  const injectionDetails = [
    `🧪 Add ${metrics.reconstitutionAmount}${metrics.reconstitutionUnits} BAC water to ${inputs.vialAmount} ${inputs.vialUnits} vial of ${inputs.peptideName}.`,
    `💉 Draw ${metrics.unitsPerDose} units on an insulin syringe (${metrics.doseMilliliters}mL) per dose.`,
    `📊 ${metrics.totalDoses} total doses.`,
    `⚗️ Final concentration: ${metrics.finalConcentration} ${inputs.vialUnits}/mL.`
  ]
    .join("\\n"); // Use escaped newline
  
  return {
    description: baseDescription,
    injectionDetails
  };
}
